import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/toast';
import { getUserGroups } from '../utils';
import {
  getClients,
  getClientAccessCredentials,
  getClientContracts,
  getClientContractFile,
  getClientW9Copy,
  lockClientAccount,
  unlockClientAccount,
  editClientInfo,
  updateClientPasswordAdmin,
  getAvailableEmployeesForAssignment,
  assignClientToEmployee,
  unassignClientFromEmployee,
} from '../services/api';

export const useClientOperations = () => {
  const navigate = useNavigate();

  const [credentialsLoader, setCredentialsLoader] = useState(false);
  const [agreementsLoader, setAgreementsLoader] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientAccessCredentials, setClientAccessCredentials] = useState([]);
  const [clientContracts, setClientContracts] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);

  const [clientAccessCredentialsModalOpen, setClientAccessCredentialsModalOpen] = useState(false);
  const [clientContractsModalOpen, setClientContractsModalOpen] = useState(false);
  const [clientLockAccountModalOpen, setClientLockAccountModalOpen] = useState(false);
  const [clientResetPasswordModalOpen, setClientResetPasswordModalOpen] = useState(false);
  const [clientEditAccountModalOpen, setClientEditAccountModalOpen] = useState(false);

  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [currentAssignmentClient, setCurrentAssignmentClient] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [unassignConfirmDialog, setUnassignConfirmDialog] = useState(false);
  const [clientToUnassign, setClientToUnassign] = useState(null);

  const [selectedClientAccountStatus, setSelectedClientAccountStatus] = useState('');
  const [selectedClientAction, setSelectedClientAction] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState({});

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    page_size: 10,
    has_next: false,
    has_previous: false,
  });
  const [stats, setStats] = useState({
    total_clients: 0,
    active_clients: 0,
    locked_clients: 0,
    inactive_clients: 0,
    assigned_clients: 0,
    unassigned_clients: 0,
  });

  const clearSelectedAccount = () => {
    setSelectedClientAction('');
    setSelectedClientId('');
    setSelectedClientAccountStatus('');
  };

  const handleLoadClients = useCallback(
    async (pageNum = pagination?.current_page, search = '', pageSize = 10) => {
      try {
        const response = await getClients(pageNum, pageSize, search);

        if (response.data.status === 'success') {
          setClients(response.data.results);
          setPagination(response.data.pagination_info);
          setStats(response.data.stats);
        } else {
          throw new Error(response.data.message || 'Failed to fetch clients');
        }
      } catch (error) {
        const message = 'Cannot get clients, try again later.';
        toast.error(message);
        console.error('Error loading clients:', error);
      }
    },
    [pagination?.current_page]
  );

  const handleClientCredentialsModalOpen = useCallback(() => {
    setCredentialsLoader(true);
    getClientAccessCredentials({ client_id: selectedClientId })
      .then((response) => {
        setClientAccessCredentials(response.data.credentials);
      })
      .catch((error) => {
        console.log('Error getting client access credentials.', error);
        toast.error('Unable to get client access credentials.');
      })
      .finally(() => {
        setCredentialsLoader(false);
      });
    setClientAccessCredentialsModalOpen(true);
  }, [selectedClientId]);

  const handleClientContractsModalOpen = useCallback(() => {
    setAgreementsLoader(true);
    getClientContracts({ client_id: selectedClientId })
      .then((response) => {
        setClientContracts(response.data.contracts);
      })
      .catch((error) => {
        console.log('Error getting client access credentials.', error);
        toast.error('Unable to get client access credentials.');
      })
      .finally(() => {
        setAgreementsLoader(false);
      });
    setClientContractsModalOpen(true);
  }, [selectedClientId]);

  const handleClientDownloadW9Copy = useCallback(async () => {
    await getClientW9Copy({ client_id: selectedClientId })
      .then((response) => {
        window.open(response.data.url, '_blank');
      })
      .catch(() => {
        const message = 'Error downloading W9 copy. Try again later.';
        toast.error(message);
      })
      .finally(() => {
        clearSelectedAccount();
      });
  }, [selectedClientId]);

  const handleClientLockAccount = () => {
    setConfirmLoader(true);
    if (selectedClientAccountStatus === 'Active') {
      lockClientAccount({ client_id: selectedClientId })
        .then(() => {
          const message = 'Client account has been locked.';
          toast.success(message);
        })
        .catch(() => {
          const message = 'Error locking client account. Try again later.';
          toast.error(message);
        })
        .finally(() => {
          setConfirmLoader(false);
          handleClientLockAccountModalClose();
        });
    }
    if (selectedClientAccountStatus === 'Locked') {
      unlockClientAccount({ client_id: selectedClientId })
        .then(() => {
          const message = 'Client account has been unlocked.';
          toast.success(message);
        })
        .catch(() => {
          const message = 'Error unlocking client account. Try again later.';
          toast.error(message);
        })
        .finally(() => {
          handleClientLockAccountModalClose();
        });
    }
  };

  const handleContractDownload = (id) => {
    getClientContractFile({ id })
      .then((response) => {
        window.open(response.data.url, '_blank');
      })
      .catch(() => {
        const message = 'Error downloading contract. Try again later.';
        toast.error(message);
      });
  };

  const handleAssignmentDialogOpen = async (client) => {
    setCurrentAssignmentClient(client);
    try {
      const response = await getAvailableEmployeesForAssignment();
      const employees = response.data ? response.data.results : [];
      setAvailableEmployees(employees);
      setAssignmentDialog(true);
    } catch (error) {
      console.error('Failed to load available employees:', error);
      setAvailableEmployees([]);
    }
  };

  const handleAssignmentDialogClose = () => {
    setAssignmentDialog(false);
    setCurrentAssignmentClient(null);
    setSelectedEmployee(null);
    setAvailableEmployees([]);
  };

  const handleAssignClient = async () => {
    if (!selectedEmployee || !currentAssignmentClient) return;

    try {
      await assignClientToEmployee(currentAssignmentClient.id, selectedEmployee.id);
      await handleLoadClients();
      handleAssignmentDialogClose();
    } catch (error) {
      console.error('Failed to assign client:', error);
    }
  };

  const handleUnassignClient = (client) => {
    setClientToUnassign(client);
    setUnassignConfirmDialog(true);
  };

  const handleUnassignConfirm = async () => {
    if (!clientToUnassign) return;

    try {
      await unassignClientFromEmployee(clientToUnassign.id);
      await handleLoadClients();
      setUnassignConfirmDialog(false);
      setClientToUnassign(null);
    } catch (error) {
      console.error('Failed to unassign client:', error);
    }
  };

  const handleUnassignCancel = () => {
    setUnassignConfirmDialog(false);
    setClientToUnassign(null);
  };

  const handleClientLockAccountModalOpen = useCallback(() => {
    setClientLockAccountModalOpen(true);
  }, []);

  const handleClientLockAccountModalClose = () => {
    setClientLockAccountModalOpen(false);
    clearSelectedAccount();
  };

  const handleClientEditAccountModalOpen = useCallback(() => {
    setClientEditAccountModalOpen(true);
  }, []);

  const handleClientEditAccountModalClose = () => {
    setClientEditAccountModalOpen(false);
    clearSelectedAccount();
  };

  const handleClientCredentialsModalClose = () => {
    setClientAccessCredentialsModalOpen(false);
    clearSelectedAccount();
  };

  const handleClientContractsModalClose = () => {
    setClientContractsModalOpen(false);
    clearSelectedAccount();
  };

  const handleResetClientPasswordModalOpen = useCallback(() => {
    setClientResetPasswordModalOpen(true);
  }, []);

  const handleClientResetPasswordModalClose = () => {
    setClientResetPasswordModalOpen(false);
    clearSelectedAccount();
  };

  const handleClientActions = (action, clientId, clientStatus, client) => {
    setSelectedClient(client);
    setSelectedClientId(clientId);
    setSelectedClientAction(action);
    setSelectedClientAccountStatus(clientStatus);
  };

  const handleEditClientSubmit = async (values) => {
    setLoading(true);
    await editClientInfo(values)
      .then(() => {
        const message = 'Client info successfully updated.';
        toast.success(message);
      })
      .catch((error) => {
        console.log('error', error);
        toast.error('Error updating client info. Try again later.');
      })
      .finally(() => {
        setLoading(false);
        handleClientEditAccountModalClose();
      });
  };

  const handleResetPasswordSubmit = async (values) => {
    setLoading(true);
    values.client_id = selectedClientId;
    await updateClientPasswordAdmin(values)
      .then(() => {
        const message = 'Client password successfully updated.';
        toast.success(message);
      })
      .catch((error) => {
        console.log('error', error);
        toast.error('Error updating password. Try again later.');
      })
      .finally(() => {
        setLoading(false);
        handleClientResetPasswordModalClose();
      });
  };

  useEffect(() => {
    setPageLoader(true);
    const userGroups = getUserGroups();
    if (!userGroups.includes('admin')) {
      navigate('/dashboard', { replace: true });
    } else {
      handleLoadClients(1, '').finally(() => {
        setPageLoader(false);
      });
    }
  }, [navigate, handleLoadClients]);

  useEffect(() => {
    if (selectedClientAction !== '') {
      switch (selectedClientAction) {
        case 'showCredentials':
          handleClientCredentialsModalOpen();
          break;
        case 'showContracts':
          handleClientContractsModalOpen();
          break;
        case 'createContract':
          navigate('/contracts/new', { replace: true });
          break;
        case 'resetClientPassword':
          handleResetClientPasswordModalOpen();
          break;
        case 'lockAccount':
          handleClientLockAccountModalOpen();
          break;
        case 'editAccount':
          handleClientEditAccountModalOpen();
          break;
        case 'downloadW9Copy':
          handleClientDownloadW9Copy();
          break;
        default:
          break;
      }
    }
  }, [
    selectedClientAction,
    handleClientCredentialsModalOpen,
    handleClientContractsModalOpen,
    navigate,
    handleResetClientPasswordModalOpen,
    handleClientLockAccountModalOpen,
    handleClientEditAccountModalOpen,
    handleClientDownloadW9Copy,
  ]);

  return {
    credentialsLoader,
    agreementsLoader,
    pageLoader,
    confirmLoader,
    loading,
    setLoading,

    clients,
    clientAccessCredentials,
    clientContracts,
    availableEmployees,
    stats,
    pagination,

    clientAccessCredentialsModalOpen,
    clientContractsModalOpen,
    clientLockAccountModalOpen,
    clientResetPasswordModalOpen,
    clientEditAccountModalOpen,
    assignmentDialog,
    unassignConfirmDialog,

    selectedClient,
    selectedClientId,
    selectedClientAccountStatus,
    currentAssignmentClient,
    selectedEmployee,
    clientToUnassign,

    handleLoadClients,
    handleClientCredentialsModalOpen,
    handleClientCredentialsModalClose,
    handleClientContractsModalOpen,
    handleClientContractsModalClose,
    handleClientLockAccountModalOpen,
    handleClientLockAccountModalClose,
    handleClientEditAccountModalOpen,
    handleClientEditAccountModalClose,
    handleResetClientPasswordModalOpen,
    handleClientResetPasswordModalClose,
    handleClientDownloadW9Copy,
    handleClientLockAccount,
    handleContractDownload,
    handleClientActions,
    handleAssignmentDialogOpen,
    handleAssignmentDialogClose,
    handleAssignClient,
    handleUnassignClient,
    handleUnassignConfirm,
    handleUnassignCancel,
    setSelectedEmployee,
    setClientToUnassign,
    handleEditClientSubmit,
    handleResetPasswordSubmit,
  };
};
